﻿import { create } from 'zustand';
import type {
  AuthSession,
  MonthFocus,
  Plan,
  PlanFilters,
  PlanViewMode,
  Profile,
  Space,
  SpaceInvite,
  SyncStatus,
  UserPreferences,
} from '@/domain/types';
import { todayKey } from '@/domain/dates';
import {
  getCurrentSession,
  logoutLocal,
  plansRepo,
  spaceRepo,
  preferencesRepo,
  monthFocusRepo,
  profileRepo,
  demoLogin,
} from '@/repositories';
import { clearPrivateCache, getMeta, setMeta, DEFAULT_BACKGROUND, getDb } from '@/storage/db';
import { createPlanDefaults, softDeletePlan, restorePlan, nowIso, createId, partnerOf, duplicatePlan } from '@/domain/helpers';
import { canMutatePlan } from '@/domain/plans';
import { deriveSyncState, listSyncQueue, summarizeQueue } from '@/sync/engine';
import { config } from '@/app/config';
import { setAppLanguage } from '@/localization/i18n';
import {
  cacheWallpaperFromStorage,
  getWallpaperSource,
  normalizeWallpaperSource,
  removeSharedWallpaper,
  resolveOwnWallpaperPublicUrl,
  resolvePartnerWallpaperUrl,
  setWallpaperSourceMeta,
  uploadSharedWallpaper,
  type WallpaperSource,
} from '@/platform/wallpapers';
import { isSupportedImageFile } from '@/platform/images';

const MAX_BACKGROUND_BYTES = 6 * 1024 * 1024;

function revokeIfBlobUrl(url: string | null | undefined): void {
  if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
}

interface AppState {
  ready: boolean;
  session: AuthSession | null;
  profiles: Profile[];
  space: Space | null;
  invite: SpaceInvite | null;
  plans: Plan[];
  monthFocus: MonthFocus[];
  preferences: UserPreferences | null;
  selectedDate: string | null;
  cursorMonth: string;
  viewMode: PlanViewMode;
  filters: PlanFilters;
  syncStatus: SyncStatus;
  online: boolean;
  lastDeleted: Plan | null;
  backgroundUrl: string | null;
  /** Stable key for current wallpaper display; skip blob recycle when unchanged. */
  backgroundCacheKey: string | null;
  wallpaperSource: WallpaperSource;
  mobileTab: 'today' | 'calendar' | 'month' | 'shared' | 'search' | 'settings';
  rightPanelOpen: boolean;
  planEditorOpen: boolean;
  editingPlanId: string | null;
  searchQuery: string;
  spaceReady: boolean;
  bootstrap: () => Promise<void>;
  loginDemo: (who: 'alex' | 'mia') => Promise<void>;
  logout: () => Promise<void>;
  refreshSpaceData: () => Promise<void>;
  setSelectedDate: (date: string | null) => void;
  setCursorMonth: (ym: string) => void;
  setViewMode: (mode: PlanViewMode) => void;
  setFilters: (partial: Partial<PlanFilters>) => void;
  setMobileTab: (tab: AppState['mobileTab']) => void;
  setRightPanelOpen: (open: boolean) => void;
  openPlanEditor: (planId?: string | null) => void;
  closePlanEditor: () => void;
  quickAddPlan: (title: string, date?: string | null) => Promise<Plan | null>;
  savePlan: (plan: Plan) => Promise<void>;
  deletePlan: (id: string) => Promise<void>;
  undoDelete: () => Promise<void>;
  movePlanToDate: (id: string, date: string | null) => Promise<void>;
  /** Clone plan onto another day (new id/clientId). Copy is always owned by the current user. */
  copyPlanToDate: (id: string, date: string | null) => Promise<Plan | null>;
  updatePreferences: (partial: Partial<UserPreferences>) => Promise<void>;
  updateProfile: (partial: Partial<Profile>) => Promise<void>;
  setBackgroundBlob: (blob: Blob | null) => Promise<void>;
  setWallpaperSource: (source: WallpaperSource) => Promise<void>;
  refreshDisplayedBackground: () => Promise<void>;
  refreshSyncStatus: () => Promise<void>;
  setOnline: (online: boolean) => void;
  syncNow: () => Promise<void>;
}

const defaultFilters: PlanFilters = {
  owners: 'all',
  visibility: 'all',
  status: 'active',
  availability: 'all',
  query: '',
};

function currentYm(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

async function loadOwnBackgroundUrl(storagePath?: string | null): Promise<string | null> {
  const prefsId = (await getMeta<string>('bg_image_id')) ?? null;
  if (prefsId) {
    const row = await getDb().backgrounds.get(prefsId);
    if (row) return URL.createObjectURL(row.blob);
  }
  if (storagePath) {
    const cached = await cacheWallpaperFromStorage(storagePath);
    if (cached) return cached.url;
    return resolveOwnWallpaperPublicUrl(storagePath);
  }
  return null;
}

async function resolveDisplayedBackgroundUrl(
  source: WallpaperSource,
  profiles: Profile[],
  userId: string | null,
  storagePath?: string | null,
): Promise<string | null> {
  if (source === 'none') return null;
  if (source === 'own') return loadOwnBackgroundUrl(storagePath);
  if (!userId) return null;
  const partner = partnerOf(profiles, userId);
  if (!partner) return null;
  return resolvePartnerWallpaperUrl(partner.id, partner.updatedAt);
}

/** One-time: push local-only wallpaper blob to Storage + prefs.storagePath. */
async function migrateLocalWallpaperIfNeeded(
  preferences: UserPreferences | null,
  userId: string | null,
): Promise<UserPreferences | null> {
  if (!userId || !preferences) return preferences;
  const bg = preferences.background ?? DEFAULT_BACKGROUND;
  if (bg.storagePath) return preferences;
  const localId = await getMeta<string>('bg_image_id');
  if (!localId) return preferences;
  const row = await getDb().backgrounds.get(localId);
  if (!row) return preferences;
  try {
    const file = new File([row.blob], 'wallpaper.jpg', { type: row.mimeType || 'image/jpeg' });
    const storagePath = await uploadSharedWallpaper(userId, file);
    const next: UserPreferences = {
      ...preferences,
      background: {
        ...bg,
        storagePath,
        source: bg.source === 'partner' ? 'partner' : 'own',
        imageId: localId,
      },
      updatedAt: nowIso(),
    };
    await preferencesRepo.upsert(next);
    await setWallpaperSourceMeta(next.background.source);
    return next;
  } catch {
    return preferences;
  }
}

async function afterLocalMutation(): Promise<void> {
  const state = useAppStore.getState();
  await state.refreshSyncStatus();
  if (config.mode === 'production' && state.online && navigator.onLine) {
    useAppStore.setState({ syncStatus: 'syncing' });
    try {
      const { requestSyncNow } = await import('@/sync/supabaseSync');
      const summary = summarizeQueue(await listSyncQueue());
      // Force when queue already has errors so exhausted zombies cannot block status forever.
      await requestSyncNow({
        force: summary.hasPermanentError || summary.hasRetryableError,
      });
      const session = useAppStore.getState().session;
      const sid = await getMeta<string>('current_space_id');
      if (session && sid) await reloadLocalSpace(sid, session.user.id);
    } catch {
      // keep pending/error status
    } finally {
      await useAppStore.getState().refreshSyncStatus();
    }
  }
}

async function applyLocalSpaceState(sid: string, userId: string): Promise<void> {
  const [space, profiles, plans, monthFocus, invite, preferences] = await Promise.all([
    spaceRepo.getSpace(sid),
    spaceRepo.getProfilesForSpace(sid),
    plansRepo.listForSpace(sid),
    monthFocusRepo.list(sid),
    spaceRepo.getInvite(sid),
    preferencesRepo.get(userId),
  ]);

  let inviteWithHint = invite ?? null;
  if (inviteWithHint && !inviteWithHint.codeHint) {
    const lastCode = await getMeta<string>('last_invite_code');
    if (lastCode) inviteWithHint = { ...inviteWithHint, codeHint: lastCode };
  }

  if (preferences?.locale) {
    await setAppLanguage(preferences.locale);
  }
  useAppStore.setState({
    space: space ?? null,
    profiles,
    plans,
    monthFocus,
    invite: inviteWithHint,
    preferences: preferences ?? null,
    spaceReady: true,
  });

  let prefs = preferences ?? null;
  prefs = await migrateLocalWallpaperIfNeeded(prefs, userId);
  if (prefs && prefs !== preferences) {
    useAppStore.setState({ preferences: prefs });
  }

  const sourceFromPrefs = normalizeWallpaperSource(prefs?.background?.source);
  const source =
    sourceFromPrefs !== 'none'
      ? sourceFromPrefs
      : await getWallpaperSource();
  if (source !== useAppStore.getState().wallpaperSource) {
    useAppStore.setState({ wallpaperSource: source });
    await setWallpaperSourceMeta(source);
  }

  // Cache own wallpaper from Storage when this device has no local blob yet.
  if (
    source === 'own' &&
    prefs?.background?.storagePath &&
    !(await getMeta<string>('bg_image_id'))
  ) {
    try {
      const cached = await cacheWallpaperFromStorage(prefs.background.storagePath);
      if (cached) {
        const nextBg = {
          ...(prefs.background ?? DEFAULT_BACKGROUND),
          imageId: cached.id,
          storagePath: prefs.background.storagePath,
          source: 'own' as const,
        };
        const nextPrefs = { ...prefs, background: nextBg, updatedAt: nowIso() };
        await preferencesRepo.upsert({
          ...nextPrefs,
          // Avoid re-enqueue storm: local-only imageId update still goes through repo.
        });
        useAppStore.setState({ preferences: nextPrefs });
      }
    } catch {
      // Offline: keep remote public URL resolution in refreshDisplayedBackground.
    }
  }

  void useAppStore.getState().refreshDisplayedBackground();
}

async function reloadLocalSpace(sid: string, userId: string): Promise<void> {
  await applyLocalSpaceState(sid, userId);
}

export const useAppStore = create<AppState>((set, get) => ({
  ready: false,
  session: null,
  profiles: [],
  space: null,
  invite: null,
  plans: [],
  monthFocus: [],
  preferences: null,
  selectedDate: todayKey(),
  cursorMonth: currentYm(),
  viewMode: 'month',
  filters: defaultFilters,
  syncStatus: 'synced',
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  lastDeleted: null,
  backgroundUrl: null,
  backgroundCacheKey: null,
  wallpaperSource: 'none',
  mobileTab: 'calendar',
  rightPanelOpen: false,
  planEditorOpen: false,
  editingPlanId: null,
  searchQuery: '',
  spaceReady: false,

  bootstrap: async () => {
    if (config.mode === 'demo') {
      // ensure seed happens lazily on login; nothing required
    }
    const session = await getCurrentSession();
    const wallpaperSource = await getWallpaperSource();
    set({ session, wallpaperSource, ready: true, spaceReady: !session });
    if (session) {
      await get().refreshSpaceData();
      if (config.mode === 'production' && get().online) {
        void get().syncNow();
      }
    }
    await get().refreshDisplayedBackground();
    await get().refreshSyncStatus();
  },

  loginDemo: async (who) => {
    const session = await demoLogin(who);
    set({ session, spaceReady: false });
    await get().refreshSpaceData();
  },

  logout: async () => {
    if (config.mode === 'production') {
      const { unsubscribeSpaceRealtime } = await import('@/sync/supabaseSync');
      await unsubscribeSpaceRealtime();
    }
    await logoutLocal();
    await clearPrivateCache();
    revokeIfBlobUrl(get().backgroundUrl);
    set({
      session: null,
      profiles: [],
      space: null,
      invite: null,
      plans: [],
      monthFocus: [],
      preferences: null,
      backgroundUrl: null,
      backgroundCacheKey: null,
      wallpaperSource: 'none',
      lastDeleted: null,
      spaceReady: false,
      syncStatus: 'synced',
    });
  },

  refreshSpaceData: async () => {
    const session = get().session;
    if (!session) return;

    if (config.mode === 'production') {
      try {
        const {
          flushSyncQueue,
          hydrateFromServer,
          hydrateUserBasics,
          subscribeSpaceRealtime,
        } = await import('@/sync/supabaseSync');
        await flushSyncQueue();
        await hydrateUserBasics(session.user.id);
        const hydratedSid = await hydrateFromServer(session.user.id);
        if (hydratedSid) {
          await subscribeSpaceRealtime(hydratedSid, () => {
            void reloadLocalSpace(hydratedSid, session.user.id);
          });
        }
      } catch {
        // Fall through to local Dexie read
      }
    }

    const spaceId = await getMeta<string>('current_space_id');
    const allMembers = await getDb().spaceMembers.where('userId').equals(session.user.id).toArray();
    const active = allMembers.find((m) => m.memberStatus === 'active');
    const sid = spaceId ?? active?.spaceId ?? null;

    if (!sid) {
      const [selfProfile, preferences] = await Promise.all([
        profileRepo.get(session.user.id),
        preferencesRepo.get(session.user.id),
      ]);
      if (preferences?.locale) {
        await setAppLanguage(preferences.locale);
      }
      set({
        space: null,
        profiles: selfProfile ? [selfProfile] : [],
        plans: [],
        monthFocus: [],
        invite: null,
        preferences: preferences ?? null,
        spaceReady: true,
      });
      return;
    }

    await applyLocalSpaceState(sid, session.user.id);
  },

  setSelectedDate: (date) => set({ selectedDate: date, rightPanelOpen: Boolean(date) }),
  setCursorMonth: (ym) => set({ cursorMonth: ym }),
  setViewMode: (mode) => set({ viewMode: mode }),
  setFilters: (partial) => set({ filters: { ...get().filters, ...partial } }),
  setMobileTab: (tab) => set({ mobileTab: tab }),
  setRightPanelOpen: (open) => set({ rightPanelOpen: open }),
  openPlanEditor: (planId = null) => set({ planEditorOpen: true, editingPlanId: planId }),
  closePlanEditor: () => set({ planEditorOpen: false, editingPlanId: null }),

  quickAddPlan: async (title, date) => {
    const { session, space } = get();
    if (!session || !space || !title.trim()) return null;
    const plan = createPlanDefaults({
      spaceId: space.id,
      ownerId: session.user.id,
      title,
      planDate: date === undefined ? get().selectedDate : date,
    });
    await plansRepo.upsert(plan);
    set({ plans: [...get().plans.filter((p) => p.id !== plan.id), plan] });
    await afterLocalMutation();
    return plan;
  },

  savePlan: async (plan) => {
    const session = get().session;
    if (!session || !canMutatePlan(plan, session.user.id)) return;
    const ts = nowIso();
    const updated = { ...plan, updatedAt: ts, clientUpdatedAt: ts };
    await plansRepo.upsert(updated);
    set({
      plans: [...get().plans.filter((p) => p.id !== updated.id), updated],
      planEditorOpen: false,
      editingPlanId: null,
    });
    await afterLocalMutation();
  },

  deletePlan: async (id) => {
    const plan = get().plans.find((p) => p.id === id);
    const session = get().session;
    if (!plan || !session || !canMutatePlan(plan, session.user.id)) return;
    const deleted = softDeletePlan(plan);
    await plansRepo.upsert(deleted);
    set({
      plans: get().plans.map((p) => (p.id === id ? deleted : p)),
      lastDeleted: deleted,
    });
    await afterLocalMutation();
  },

  undoDelete: async () => {
    const last = get().lastDeleted;
    if (!last) return;
    const restored = restorePlan(last);
    await plansRepo.upsert(restored);
    set({
      plans: get().plans.map((p) => (p.id === restored.id ? restored : p)),
      lastDeleted: null,
    });
    await afterLocalMutation();
  },

  movePlanToDate: async (id, date) => {
    const plan = get().plans.find((p) => p.id === id);
    const session = get().session;
    if (!plan || !session || !canMutatePlan(plan, session.user.id)) return;
    const ts = nowIso();
    const updated = { ...plan, planDate: date, updatedAt: ts, clientUpdatedAt: ts };
    await plansRepo.upsert(updated);
    set({ plans: get().plans.map((p) => (p.id === id ? updated : p)) });
    await afterLocalMutation();
  },

  copyPlanToDate: async (id, date) => {
    const plan = get().plans.find((p) => p.id === id);
    const session = get().session;
    if (!plan || !session || !canMutatePlan(plan, session.user.id)) return null;
    const copy: Plan = {
      ...duplicatePlan(plan),
      ownerId: session.user.id,
      planDate: date,
    };
    await plansRepo.upsert(copy);
    set({ plans: [...get().plans.filter((p) => p.id !== copy.id), copy] });
    await afterLocalMutation();
    return copy;
  },

  updatePreferences: async (partial) => {
    const session = get().session;
    if (!session) return;
    const current = get().preferences;
    const { background: bgPartial, notifications: notifPartial, ...restPartial } = partial;
    const {
      background: _cb,
      notifications: _cn,
      updatedAt: _ua,
      ...currentRest
    } = current ?? {};
    void _cb;
    void _cn;
    void _ua;
    const next: UserPreferences = {
      userId: session.user.id,
      locale: 'en',
      timeFormat: '24h',
      weekStartsOn: 1,
      onboardingCompleted: false,
      shortcutsHintSeen: false,
      ...currentRest,
      ...restPartial,
      background: { ...(current?.background ?? DEFAULT_BACKGROUND), ...(bgPartial ?? {}) },
      notifications: {
        ...(current?.notifications ?? { enabled: false, offsets: [] }),
        ...(notifPartial ?? {}),
      },
      updatedAt: nowIso(),
    };
    await preferencesRepo.upsert(next);
    if (partial.locale) await setAppLanguage(partial.locale);
    set({ preferences: next });
    await afterLocalMutation();
  },

  updateProfile: async (partial) => {
    const session = get().session;
    if (!session) return;
    const current = get().profiles.find((p) => p.id === session.user.id);
    if (!current) return;
    const next = { ...current, ...partial, updatedAt: nowIso() };
    await profileRepo.upsert(next);
    set({ profiles: get().profiles.map((p) => (p.id === next.id ? next : p)) });
    await afterLocalMutation();
  },

  setBackgroundBlob: async (blob) => {
    const db = getDb();
    const session = get().session;
    const existingId = await getMeta<string>('bg_image_id');
    if (existingId) await db.backgrounds.delete(existingId);
    if (!blob) {
      await setMeta('bg_image_id', null);
      if (session) {
        try {
          await removeSharedWallpaper(session.user.id);
        } catch {
          // local clear still applies
        }
      }
      await setWallpaperSourceMeta('none');
      set({ wallpaperSource: 'none' });
      await get().updatePreferences({
        background: {
          ...(get().preferences?.background ?? DEFAULT_BACKGROUND),
          imageId: null,
          storagePath: null,
          source: 'none',
        },
      });
      await get().refreshDisplayedBackground();
      return;
    }
    if (blob.size > MAX_BACKGROUND_BYTES) {
      throw new Error('Background image too large');
    }
    if (
      !isSupportedImageFile(
        blob instanceof File ? blob : new File([blob], 'bg', { type: blob.type }),
      )
    ) {
      throw new Error('Unsupported background type');
    }
    const file =
      blob instanceof File
        ? blob
        : new File([blob], 'wallpaper.jpg', { type: blob.type || 'image/jpeg' });
    const id = createId();
    await db.backgrounds.put({
      id,
      blob: file,
      mimeType: file.type || 'image/jpeg',
      createdAt: nowIso(),
    });
    await setMeta('bg_image_id', id);

    let storagePath: string | null = null;
    if (session) {
      try {
        storagePath = await uploadSharedWallpaper(session.user.id, file);
      } catch {
        // Local wallpaper still works offline.
      }
    }

    await setWallpaperSourceMeta('own');
    set({ wallpaperSource: 'own' });
    await get().updatePreferences({
      background: {
        ...(get().preferences?.background ?? DEFAULT_BACKGROUND),
        imageId: id,
        storagePath,
        source: 'own',
      },
    });
    await get().refreshDisplayedBackground();
  },

  setWallpaperSource: async (source) => {
    if (source === 'own') {
      const ownId = await getMeta<string>('bg_image_id');
      const storagePath = get().preferences?.background?.storagePath;
      if (!ownId && !storagePath) throw new Error('no_own_wallpaper');
    }
    if (source === 'partner') {
      const session = get().session;
      const partner = session ? partnerOf(get().profiles, session.user.id) : null;
      if (!partner) throw new Error('no_partner');
      const url = await resolvePartnerWallpaperUrl(partner.id, partner.updatedAt);
      if (!url) throw new Error('partner_wallpaper_missing');
    }
    await setWallpaperSourceMeta(source);
    set({ wallpaperSource: source });
    await get().updatePreferences({
      background: {
        ...(get().preferences?.background ?? DEFAULT_BACKGROUND),
        source,
      },
    });
    await get().refreshDisplayedBackground();
  },

  refreshDisplayedBackground: async () => {
    const source = get().wallpaperSource;
    const session = get().session;
    const storagePath = get().preferences?.background?.storagePath ?? null;
    const localImageId = (await getMeta<string>('bg_image_id')) ?? '';
    const partner =
      source === 'partner' && session
        ? partnerOf(get().profiles, session.user.id)
        : null;
    const cacheKey = [
      source,
      storagePath ?? '',
      localImageId,
      partner?.id ?? '',
      partner?.updatedAt ?? '',
    ].join('|');

    // Same wallpaper identity → keep existing object/public URL (no revoke flash).
    if (cacheKey === get().backgroundCacheKey && get().backgroundUrl) {
      return;
    }

    const prev = get().backgroundUrl;
    let next: string | null = null;
    try {
      next = await resolveDisplayedBackgroundUrl(
        source,
        get().profiles,
        session?.user.id ?? null,
        storagePath,
      );
    } catch {
      next = null;
    }
    if (prev !== next) {
      revokeIfBlobUrl(prev);
    }
    set({ backgroundUrl: next, backgroundCacheKey: cacheKey });
  },

  refreshSyncStatus: async () => {
    const queue = await listSyncQueue();
    const summary = summarizeQueue(queue);
    const status = deriveSyncState({
      online: get().online,
      syncing: false,
      pendingCount: summary.pendingCount,
      hasPermanentError: summary.hasPermanentError,
      hasRetryableError: summary.hasRetryableError,
    });
    set({ syncStatus: status });
  },

  setOnline: (online) => {
    set({ online });
    void get().refreshSyncStatus();
    if (online && config.mode === 'production') {
      void get().syncNow();
    }
  },

  syncNow: async () => {
    if (config.mode !== 'production') return;
    set({ syncStatus: 'syncing' });
    try {
      const { requestSyncNow } = await import('@/sync/supabaseSync');
      await requestSyncNow({ force: true });
      const session = get().session;
      const sid = await getMeta<string>('current_space_id');
      if (session && sid) await reloadLocalSpace(sid, session.user.id);
    } finally {
      await get().refreshSyncStatus();
    }
  },
}));
